import { useMemo } from 'react';
// routes
import { paths } from 'src/routes/paths';
// locales
import { useLocales } from 'src/locales';
// components
import Iconify from 'src/components/iconify';
import SvgColor from 'src/components/svg-color';

// ----------------------------------------------------------------------

const icon = (name) => (
  <SvgColor src={`/static/icons/navbar/${name}.svg`} sx={{ width: 1, height: 1 }} />
);

const ICONS = {
  job: icon('ic_job'),
  blog: icon('ic_blog'),
  chat: icon('ic_chat'),
  mail: icon('ic_mail'),
  user: icon('ic_user'),
  file: icon('ic_file'),
  lock: icon('ic_lock'),
  tour: icon('ic_tour'),
  order: icon('ic_order'),
  label: icon('ic_label'),
  blank: icon('ic_blank'),
  kanban: icon('ic_kanban'),
  folder: icon('ic_folder'),
  banking: icon('ic_banking'),
  booking: icon('ic_booking'),
  invoice: icon('ic_invoice'),
  product: icon('ic_product'),
  calendar: icon('ic_calendar'),
  disabled: icon('ic_disabled'),
  external: icon('ic_external'),
  menuItem: icon('ic_menu_item'),
  ecommerce: icon('ic_ecommerce'),
  analytics: icon('ic_analytics'),
  dashboard: icon('ic_dashboard'),
};

// ----------------------------------------------------------------------

export function useNavData() {
  const { t } = useLocales();

  const data = useMemo(
    () => [
      {
        subheader: t('Assigned Worker Tasks'),
        items: [
          {
            roles: ['Worker', 'Admin'],
            title: t('Assigned fetch lists'),
            path: paths.workerdashboard.workerfetchlist.root,
            icon: <Iconify icon="lsicon:list-outline" width={24} />
            ,
            children: [
              { title: t('fetch lists'), path: paths.workerdashboard.workerfetchlist.root },
            ],
          },
          {
            roles: ['Worker', 'Admin'],
            title: t('Assigned job list'),
            path: paths.workerdashboard.workjob.root,
            icon: <Iconify icon="lsicon:list-outline" width={24} />,
            children: [
              { title: t('list'), path: paths.workerdashboard.workjob.root },
            ],
          },
        ]
      },
      {
        subheader: t('WMS'),
        items: [
          // ORGANISATION
          {
            title: t('organisation'),
            roles: ['Admin'],
            path: paths.dashboard.warehouse.root,
            icon: <Iconify icon="charm:organisation" width={24} />,
            children: [
              { title: t('list'), path: paths.dashboard.warehouse.root },
              // { title: t('create'), path: paths.dashboard.warehouse.new },
            ],
          },
          // PRODUCT CATEGORY
          {
            title: t('product categories'),
            roles: ['Admin'],
            path: paths.dashboard.productcatogory.root,
            icon: <Iconify icon="material-symbols:category" width={24} />,
            children: [
              {
                title: t('list'),
                path: paths.dashboard.productcatogory.root,
                children: [
                  { title: t('general list'), path: paths.dashboard.productcatogory.root },
                  { title: t('generic list'), path: paths.dashboard.genericcategory.root },
                ]
              },
              {
                title: t('create'),
                path: paths.dashboard.productcatogory.new,
                children: [
                  { title: t('general create'), path: paths.dashboard.productcatogory.new, },
                  { title: t('generic create'), path: paths.dashboard.genericcategory.new },
                ]
              },
            ],
          },
          // PRODUCT
          {
            title: t('products'),
            roles: ['Admin'],
            path: paths.dashboard.product.root,
            icon: <Iconify icon="fluent-mdl2:product-variant" width={24} />,
            children: [
              {
                title: t('list'),
                path: paths.dashboard.product.root,
                children: [
                  { title: t('general list'), path: paths.dashboard.product.root, },
                  { title: t('generic list'), path: paths.dashboard.genericproduct.root },
                ]
              },
              {
                title: t('create'),
                path: paths.dashboard.product.new,
                children: [
                  { title: t('general create'), path: paths.dashboard.product.new },
                  { title: t('generic create'), path: paths.dashboard.genericproduct.new },
                ]
              },
              {
                title: t('logs'),
                path: paths.dashboard.product.productlog.root
              },
            ],
          },
          {
            title: t('final products'),
            roles: ['Admin'],
            path: paths.dashboard.finalproduct.list,
            icon: <Iconify icon="fluent-mdl2:product-variant" width={24} />,
            children: [
              { title: t('list'), path: paths.dashboard.finalproduct.list },
              { title: t('create'), path: paths.dashboard.finalproduct.new },
            ],
          },
          // INVENTORY
          {
            title: t('inventory management'),
            roles: ['Admin'],
            path: paths.dashboard.inventory.root,
            icon: <Iconify icon="si:inventory-fill" width={20} />,
            children: [
              { title: t('list'), path: paths.dashboard.inventory.root },
              { title: t('create'), path: paths.dashboard.inventory.new },
              { title: t('create variant'), path: paths.dashboard.inventory.createVariant },
              { title: t('logs'), path: paths.dashboard.inventorylog.root },
            ],
          },
          // ORDER
          {
            title: t('orders'),
            roles: ['Admin'],
            path: paths.dashboard.order.root,
            icon: ICONS.order,
            children: [
              { title: t('list'), path: paths.dashboard.order.root },
              { title: t('create'), path: paths.dashboard.order.new },
              { title: t('logs'), path: paths.dashboard.order.logs },
            ],
          },
          // FETCH LIST
          {
            title: t('fetch list'),
            roles: ['Admin'],
            path: paths.dashboard.fetchlist.root,
            icon: <Iconify icon="lsicon:list-outline" width={24} />
            ,
            children: [
              { title: t('list'), path: paths.dashboard.fetchlist.root },
              // { title: t('create'), path: paths.dashboard.fetchlist.new },
            ],
          },
          {
            title: t('workflows list'),
            roles: ['Admin'],
            path: paths.dashboard.workflowslist.root,
            icon: <Iconify icon="carbon:workflow-automation" width={24} />
            ,
            children: [
              { title: t('list'), path: paths.dashboard.workflowslist.root },
              // { title: t('create'), path: paths.dashboard.workflowslist.new },
            ],
          },
          // PROCESS
          {
            title: t('Process'),
            roles: ['Admin'],
            path: paths.dashboard.process.root,
            icon: <Iconify icon="clarity:process-on-vm-line" width={24} />,
            children: [
              { title: t('list'), path: paths.dashboard.process.list },
              { title: t('create'), path: paths.dashboard.process.new },
            ],
          },
          // WORK-ORDER
          {
            title: t('Work Order'),
            roles: ['Admin'],
            path: paths.dashboard.workorder.root,
            icon: <Iconify icon="uil:process" width={24} />,
            children: [
              { title: t('list'), path: paths.dashboard.workorder.list },
              { title: t('create'), path: paths.dashboard.workorder.new },
            ],
          },
          // QR Code
          {
            title: t('QR Code'),
            roles: ['Admin'],
            path: paths.dashboard.qrcode.root,
            icon: ICONS.invoice,
            children: [
              { title: t('Create'), path: paths.dashboard.qrcode.root },

            ],
          },
          {
            title: t('Flow Management'),
            roles: ['Admin'],
            path: paths.dashboard.flowmanagement.root,
            icon: <Iconify icon="gis:flow-map" width={24} />,
            children: [
              { title: t('list'), path: paths.dashboard.flowmanagement.list },
              // { title: t('create'), path: paths.dashboard.flowmanagement.new },
            ],
          },
          {
            title: t('Production'),
            roles: ['Admin'],
            path: paths.production,
            icon: <Iconify icon="fluent:production-20-filled" width={24} />,
            children: [
              {
                title: t('Create Flow'),
                path: paths.production
              },
              {
                title: t('Schedule Jobs'),
                path: paths.production3rdstep
              },
              {
                title: t('Jobs Stats'),
                path: paths.dashboard.jobstats
              },
              {
                title: t('Jobs list'),
                path: paths.dashboard.jobprofile.list,
                children: [
                  { title: t('Create'), path: paths.factorylayout },
                  { title: t('Machine Jobs'), path: paths.dashboard.jobprofile.list, },
                  { title: t('Non-Machine Jobs'), path: paths.workerdashboard.nodewithoutmachine.root, },
                  { title: t('Job Approvals'), path: paths.workerdashboard.decision.root },
                ],
              },
              {
                title: t('Layout'),
                path: paths.factorylayoutview,
                children: [
                  { title: t('view'), path: paths.factorylayoutview },
                  { title: t('create'), path: paths.factorylayout },
                ],
              },
              {
                title: t('costs'),
                roles: ['Admin'],
                path: paths.dashboard.costlist.list,
              },
              {
                title: t('kanban'),
                path: paths.dashboard.kanban,
                children: [
                  { title: t('List'), path: paths.dashboard.allkanban },
                  { title: t('Layout'), path: paths.dashboard.layoutkanban },
                  { title: t('All'), path: paths.dashboard.kanban },
                ],
              },
              {
                title: t('Workflow Documents'),
                path: paths.dashboard.workflowdocument.root,
              },
            ],
          },
          // {
          //   title: t('Final Production'),
          //   roles: ['Admin'],
          //   path: paths.production,
          //   icon: ICONS.invoice,
          //   children: [
          //     { title: t('Variant Initilize'), path: paths.finalproduction2ndstep },
          //     { title: t('Job Initilize'), path: paths.finalproduction3rdstep },
          //   ],
          // },
          {
            title: t('purchase orders'),
            roles: ['Admin'],
            path: paths.dashboard.purchaseorder.root,
            icon: ICONS.order,
            children: [
              { title: t('overview'), path: paths.dashboard.purchaseorder.overview },
              { title: t('list'), path: paths.dashboard.purchaseorder.list },
              { title: t('create'), path: paths.dashboard.purchaseorder.new },
            ],
          },
        ]
      },
      // {
      //   subheader: t(t('PO Workflow')),
      //   items: [
      //     {
      //       title: t('Vendor PO'),
      //       roles: ['Admin'],
      //       path: paths.dashboard.adminpo.root,
      //       icon: ICONS.order,
      //       children: [
      //         { title: t('list'), path: paths.dashboard.adminpo.root },
      //       ],
      //     },
      //     {
      //       title: t('Finished Vendor PO'),
      //       roles: ['Admin'], path: paths.dashboard.finishedvendorpo.root,
      //       icon: <Iconify icon="streamline-freehand:job-profile-search" width={24} />,
      //       children: [{ title: t('list'), path: paths.dashboard.finishedvendorpo.list },],
      //     },
      //     {
      //       title: t('Product Selection'),
      //       roles: ['Admin'],
      //       path: paths.dashboard.selectproduct.root,
      //       icon: <Iconify icon="mi:select" width={24} />,
      //       children: [
      //         { title: t('list'), path: paths.dashboard.selectproduct.list },
      //       ],
      //     },
      //     {
      //       title: t('Workflow Initialization'),
      //       roles: ['Admin'],
      //       path: paths.dashboard.workflowintialize.root,
      //       icon: <Iconify icon="streamline-freehand:job-profile-search" width={24} />,
      //       children: [
      //         { title: t('list'), path: paths.dashboard.workflowintialize.list },
      //       ],
      //     },
      //     {
      //       title: t('MS Job Approvals'),
      //       roles: ['Admin', 'Worker'],
      //       path: paths.dashboard.decision.root,
      //       icon: <Iconify icon="streamline-flex:group-meeting-approval" width={24} />,
      //     },
      //   ]
      // },
      // DEMO MENU STATES
      {
        subheader: t(t('master table')),
        items: [
          {
            title: t('clients'),
            roles: ['Admin'],
            path: paths.dashboard.client,
            icon: <Iconify icon="raphael:customer" width={24} />
          },
          {
            title: t('vendors'),
            roles: ['Admin'],
            path: paths.dashboard.vendor,
            icon: <Iconify icon="fluent:building-retail-20-filled" width={24} />
          },
          {
            title: t('submachines'),
            roles: ['Admin'],
            path: paths.dashboard.submachines,
            icon: <Iconify icon="ix:machine-a" width={24} />
          },
          {
            title: t('machines'),
            roles: ['Admin'],
            path: paths.dashboard.machines,
            icon: <Iconify icon="ix:machine-c" width={24} />
          },
          {
            title: t('roles'),
            roles: ['Admin'],
            path: paths.dashboard.roles,
            icon: <Iconify icon="oui:app-users-roles" width={24} />
          },
          {
            title: t('permission'),
            path: paths.dashboard.permission,
            icon: ICONS.lock,
            roles: ['Admin'],
          },
          {
            title: t('general cost'),
            roles: ['Admin'],
            path: paths.dashboard.cost,
            icon: <Iconify icon="mynaui:rupee-square" width={24} />
          },
          {
            title: t('generic cost'),
            roles: ['Admin'],
            path: paths.dashboard.generic_cost,
            icon: <Iconify icon="mynaui:rupee-square" width={24} />
          },
          {
            title: t('general cost list'),
            roles: ['Admin'],
            path: paths.dashboard.general_cost,
            icon: <Iconify icon="mynaui:rupee-square" width={24} />
          },
          {
            title: t('Sticker Page Configuration'),
            roles: ['Admin'],
            path: paths.dashboard.page_configuration,
            icon: <Iconify icon="mingcute:sticker-fill" width={24} />
          },
          {
            title: t('Templates'),
            roles: ['Admin'],
            path: paths.dashboard.templates.root,
            icon: <Iconify icon="tabler:template" width={24} />,
            children: [
              { title: t('QR'), path: paths.dashboard.templates.qr },
              { title: t('PO'), path: paths.dashboard.templates.po },
              { title: t('Vendor PO Template'), path: paths.dashboard.templates.vendorpo },
            ],
          },
          {
            title: t('workers'),
            roles: ['Admin'],
            path: paths.dashboard.worker,
            icon: <Iconify icon="healthicons:factory-worker" width={24} />,
          },
          {
            title: t('measurement units'),
            roles: ['Admin'],
            path: paths.dashboard.measurement,
            icon: <Iconify icon="mdi:pencil-ruler-outline" width={24} />,
          },
        ],
      },
    ],
    [t]
  );

  return data;
}